/**
 * TaxRevenuePage
 * 매출 관리 페이지
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTaxRevenues } from '../../use-cases/useTaxRevenues';
import { TaxRevenue, formatKRW } from '../../models/Tax';
import { RevenueFormModal } from './RevenueFormModal';

export const TaxRevenuePage: React.FC = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<TaxRevenue | null>(null);

  const {
    revenues,
    loading,
    error,
    summary,
    fetchRevenues,
    createRevenue,
    updateRevenue,
    deleteRevenue,
    exportCsv,
  } = useTaxRevenues();

  const loadData = useCallback(() => {
    fetchRevenues({ year, month, search: search || undefined });
  }, [fetchRevenues, year, month, search]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('이 매출을 삭제하시겠습니까?')) return;
    const ok = await deleteRevenue(id);
    if (ok) loadData();
  };

  const handleEdit = (item: TaxRevenue) => {
    setEditItem(item);
    setShowForm(true);
  };

  const handleSubmit = async (data: any) => {
    if (editItem) {
      const ok = await updateRevenue(editItem.id, data);
      if (ok) loadData();
      return ok;
    } else {
      const ok = await createRevenue(data);
      if (ok) loadData();
      return ok;
    }
  };

  const yearOptions = Array.from({ length: 4 }, (_, i) => now.getFullYear() - i);

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">매출 관리</h1>
        <div className="flex gap-2">
          <button
            onClick={() => exportCsv(year, month)}
            className="px-4 py-2 text-sm font-medium text-green-700 bg-green-50 rounded-md hover:bg-green-100"
          >
            CSV 내보내기
          </button>
          <button
            onClick={() => { setEditItem(null); setShowForm(true); }}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            + 매출 추가
          </button>
        </div>
      </div>

      {/* 요약 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">건수</p>
          <p className="text-2xl font-bold text-gray-900">{summary.count}건</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">공급가액 합계</p>
          <p className="text-2xl font-bold text-gray-900">{formatKRW(summary.totalAmount)}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">부가세 합계</p>
          <p className="text-2xl font-bold text-blue-600">{formatKRW(summary.totalVat)}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">세금계산서 발행</p>
          <p className="text-2xl font-bold text-green-600">{summary.invoicedCount}/{summary.count}건</p>
        </div>
      </div>

      {/* 필터 */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="px-3 py-2 border border-gray-300 rounded-md text-sm">
            {yearOptions.map((y) => <option key={y} value={y}>{y}년</option>)}
          </select>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="px-3 py-2 border border-gray-300 rounded-md text-sm">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{m}월</option>)}
          </select>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="거래처/내용 검색..."
            className="px-3 py-2 border border-gray-300 rounded-md text-sm flex-1 min-w-[200px]"
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* 테이블 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">날짜</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">거래처</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">내용</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">공급가액</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">부가세</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">합계</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">계산서</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">작업</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">불러오는 중...</td></tr>
              ) : revenues.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">데이터가 없습니다.</td></tr>
              ) : (
                revenues.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{item.date}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{item.client}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {item.description || '-'}
                      {item.memo && <div className="text-xs text-gray-400 truncate max-w-[200px]">{item.memo}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap">{item.amount.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-blue-600 text-right whitespace-nowrap">{item.vat.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right whitespace-nowrap">{item.total.toLocaleString()}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs ${item.invoice_issued ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {item.invoice_issued ? '발행' : '미발행'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => handleEdit(item)} className="text-xs text-blue-600 hover:text-blue-800 mr-2">수정</button>
                      <button onClick={() => handleDelete(item.id)} className="text-xs text-red-600 hover:text-red-800">삭제</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <RevenueFormModal
        isOpen={showForm}
        onClose={() => { setShowForm(false); setEditItem(null); }}
        onSubmit={handleSubmit}
        editItem={editItem}
      />
    </div>
  );
};
